import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Switch from 'common/Switch';
import SelectLanguages from 'common/SelectLanguages';
import ImageUploader from './ImageUploader';
import ImagesLibrary from './ImagesLibrary';
import {
  setStep,
  setTTPDialogCustomData,
  setCurrentNavPage,
  initMedias,
} from 'actions';
import {
  fetchTemplates,
  fetchMedias,
  saveTheme,
  fetchTemplatesCategories,
} from 'thunks';
import _ from 'i18n';
import { STEPS } from 'Common';
import { onSuccess, onError } from 'utils';
import DIALOG_TYPES from '../../constants/TTPDialog';
import { load } from 'cheerio';
import { S3_FOLDER_URL_PROD } from 'Config';
import TTPSelect from '../common/TTPSelect';

const ThemeEditor = ({ match, history }) => {
  const dispatch = useDispatch();
  const { themes, currentTheme, categories, lng, fetched } = useSelector((store) => ({
    themes: store.themes.list.items,
    currentTheme: store.themes.currentSimpleTheme,
    categories: store.themes.categories.items,
    lng: store.auth.user.language,
    fetched: store.themes.categories.fetched,
  }));

  const [state, setState] = useState({
    name: '',
    allowDeletion: false,
    allowChange: false,
    language: 'fr',
    templateId: null,
    themeContent: null,
    originalTheme: '',
    selectedImg: null,
    activePalette: null,
    changed: false,
    images: [],
    type: '',
    themeCategory: null,
  });

  const themeManagerRef = useRef(null);
  const { templateId, name, language, themeContent, activePalette, selectedImg, allowDeletion, allowChange, images, themeCategory } = state;

  useEffect(() => {
    dispatch(setCurrentNavPage('SETTINGS'));
    dispatch(setStep(STEPS['MANAGE_THEME']));
    dispatch(initMedias());
    dispatch(fetchMedias());
    dispatch(fetchTemplatesCategories());
    document.body.classList.add('bg-d8');

    const id = match.params.templateId;
    if (id && !currentTheme) {
      setState((prev) => ({ ...prev, templateId: id }));
      dispatch(fetchTemplates({ id }));
    } else if (currentTheme) {
      loadTheme(currentTheme);
    }

    return () => {
      document.body.classList.remove('bg-d8');
      close();
    };
  }, [dispatch, match.params.templateId, currentTheme]);

  useEffect(() => {
    if (templateId && themes.length > 0 && templateId === themes[0].id) {
      loadTheme(themes[0]);
    } else if (currentTheme) {
      loadTheme(currentTheme);
    }
  }, [themes, currentTheme, templateId]);

  const loadTheme = (theme) => {
    const { content, name, language, type, images, themeCategory } = theme;
    const $ = load(content);
    const themeImages = images || [];
    $('img').each((i, image) => themeImages.push($(image).attr('src')));

    setState((prev) => ({
      ...prev,
      originalTheme: content,
      name,
      language,
      themeContent: $('body').html(),
      images: themeImages,
      type,
      themeCategory,
    }));
  };

  const handlePreviewClick = (event) => {
    const target = event.target;
    if (target.nodeName !== 'IMG') return;

    if (selectedImg) selectedImg.classList.remove('img-selected');
    const newSelectedImg = target !== selectedImg ? target : null;
    newSelectedImg?.classList.add('img-selected');
    setState((prev) => ({
      ...prev,
      selectedImg: newSelectedImg,
      activePalette: newSelectedImg ? 1 : null,
      allowDeletion: newSelectedImg?.classList.contains('ttp-deletable') || false,
      allowChange: newSelectedImg?.classList.contains('ttp-alterable') || false,
    }));
  };

  const closeThemeManager = () => {
    if (!state.changed) return close();
    dispatch(
      setTTPDialogCustomData({
        approvedAction: close,
        message: _('Once you close your changes will be lost. Do you really want to continue?'),
        type: DIALOG_TYPES['ALERT'],
      })
    );
  };

  const close = () => {
    dispatch(setStep(null));
    window.parent.postMessage('CLOSE_VIEWER', '*');
    history.push('/templates');
  };

  const renderImagesLibrary = (initialOnly = false) => (
    <ImagesLibrary
      initialOnly={initialOnly}
      initialImages={images}
      selectedImg={selectedImg}
      onImageChange={() => setState((prev) => ({ ...prev, selectedImg: null, changed: true }))}
    />
  );

  return (
    <div id="theme-manager" ref={themeManagerRef}>
      <div className="row">
        <div className="style-tools-menu column small-7">
          <h3>{_('Images used')}</h3>
          <Switch
            name="can-remove"
            isChecked={allowDeletion}
            onChange={(isChecked) => setState((prev) => ({ ...prev, allowDeletion: isChecked }))}
          />
          {renderImagesLibrary(true)}
        </div>
      </div>
    </div>
  );
};

export default ThemeEditor;
